<template>
  <div class="waajo-ticket-picker">
    <div class="waajo-ticket-picker-head">
      <label>Lampiran Gambar <span class="is-optional">(opsional)</span></label>
      <span class="waajo-ticket-picker-count">{{ files.length }}/{{ maxFiles }} gambar</span>
    </div>
    <p class="waajo-ticket-picker-help">
      Gambar diunggah setelah tiket berhasil dibuat. Belum ada file yang dikirim
      ke server saat ini.
    </p>

    <ticket-attachment-grid
      v-if="previews.length"
      :items="previews"
      removable
      :disabled="disabled"
      @preview="openPreview"
      @remove="removeItem"
      @image-error="markBroken"
    />

    <ticket-attachment-dropzone
      :remaining="remaining"
      :max-files="maxFiles"
      :busy="busy"
      :progress="progress"
      :disabled="disabled"
      @files="handleFiles"
      @rejected="handleRejected"
    />

    <div v-if="validationMessages.length" class="waajo-ticket-picker-error">
      <i class="fa fa-exclamation-circle"></i>
      <div>
        <p v-for="(message, index) in validationMessages" :key="'picker-error-' + index">
          {{ message }}
        </p>
      </div>
    </div>

    <ticket-attachment-preview
      :visible="previewVisible"
      :items="previews"
      :start-index="previewIndex"
      @close="closePreview"
      @download="downloadLocal"
      @refresh="refreshPreviews"
      @image-error="markBroken"
    />
  </div>
</template>

<script>
import {
  TICKET_ATTACHMENT_MAX_FILES,
  filterTicketAttachmentFiles,
  ticketAttachmentCapacityReason
} from '@/utils/ticketAttachment'
import TicketAttachmentDropzone from './TicketAttachmentDropzone.vue'
import TicketAttachmentGrid from './TicketAttachmentGrid.vue'
import TicketAttachmentPreview from './TicketAttachmentPreview.vue'

const MAX_VALIDATION_MESSAGES = 6

export default {
  name: 'TicketAttachmentPicker',
  components: {
    TicketAttachmentDropzone,
    TicketAttachmentGrid,
    TicketAttachmentPreview
  },
  props: {
    files: {
      type: Array,
      default: () => []
    },
    maxFiles: {
      type: Number,
      default: TICKET_ATTACHMENT_MAX_FILES
    },
    disabled: {
      type: Boolean,
      default: false
    },
    busy: {
      type: Boolean,
      default: false
    },
    progress: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      previews: [],
      validationMessages: [],
      previewVisible: false,
      previewIndex: 0
    }
  },
  computed: {
    remaining() {
      return Math.max(0, this.maxFiles - (this.files ? this.files.length : 0))
    }
  },
  watch: {
    files: {
      immediate: true,
      handler() {
        this.rebuildPreviews()
      }
    }
  },
  beforeDestroy() {
    this.revokePreviewUrls()
  },
  methods: {
    revokePreviewUrls() {
      (this.previews || []).forEach(item => {
        if (item && item.url) window.URL.revokeObjectURL(item.url)
      })
      this.previews = []
    },
    rebuildPreviews() {
      this.revokePreviewUrls()
      const list = Array.isArray(this.files) ? this.files : []
      this.previews = list.map((file, index) => ({
        key:
          'local-' +
          index +
          '-' +
          String(file.name || '') +
          '-' +
          String(file.size || 0) +
          '-' +
          String(file.lastModified || 0),
        id: 'local-' + index,
        url: window.URL.createObjectURL(file),
        name: file.name || 'Gambar',
        size: file.size,
        broken: false
      }))
      if (this.previewVisible) {
        if (!this.previews.length) {
          this.previewVisible = false
        } else if (this.previewIndex > this.previews.length - 1) {
          this.previewIndex = this.previews.length - 1
        }
      }
    },
    handleFiles(fileList) {
      if (this.disabled || this.busy) return
      this.validationMessages = []

      const current = Array.isArray(this.files) ? this.files.slice() : []
      const available = Math.max(0, this.maxFiles - current.length)
      if (available <= 0) {
        this.validationMessages = [ticketAttachmentCapacityReason({ maxFiles: this.maxFiles })]
        return
      }

      const result = filterTicketAttachmentFiles(fileList, available, {
        maxFiles: this.maxFiles
      })
      if (result.rejected.length) this.handleRejected(result.rejected)
      if (!result.accepted.length) return
      this.$emit('change', current.concat(result.accepted))
    },
    handleRejected(rejected) {
      const list = Array.isArray(rejected) ? rejected : []
      this.validationMessages = list
        .slice(0, MAX_VALIDATION_MESSAGES)
        .map(item => {
          if (!item) return 'Gambar tidak dapat ditambahkan.'
          const name = String(item.name || '').trim()
          const reason = String(item.reason || 'Gambar tidak dapat ditambahkan.')
          return name ? name + ': ' + reason : reason
        })
    },
    removeItem(item) {
      if (this.disabled || this.busy || !item) return
      const index = this.previews.findIndex(preview => preview.key === item.key)
      if (index === -1) return
      const next = (this.files || []).slice()
      next.splice(index, 1)
      this.validationMessages = []
      this.$emit('change', next)
    },
    openPreview(index) {
      this.previewIndex = Math.max(0, Number(index) || 0)
      this.previewVisible = true
    },
    closePreview() {
      this.previewVisible = false
    },
    refreshPreviews() {
      this.rebuildPreviews()
    },
    markBroken(item) {
      if (!item) return
      this.previews = this.previews.map(preview => {
        if (preview.key !== item.key) return preview
        return Object.assign({}, preview, { broken: true })
      })
    },
    downloadLocal(item) {
      if (!item || !item.url) return
      const link = document.createElement('a')
      link.href = item.url
      link.setAttribute('download', item.name || 'gambar')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }
  }
}
</script>

<style scoped>
.waajo-ticket-picker {
  width: 100%;
}
.waajo-ticket-picker-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 4px;
}
.waajo-ticket-picker-head label {
  display: block;
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--waajo-text-body);
  line-height: 1.4;
}
.waajo-ticket-picker-head .is-optional {
  font-size: 12px;
  font-weight: 500;
  color: var(--waajo-text-faint);
}
.waajo-ticket-picker-count {
  padding: 2px 8px;
  border: 1px solid var(--waajo-brand-border);
  border-radius: 9999px;
  background: var(--waajo-brand-soft-2);
  color: var(--waajo-brand-ink);
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}
.waajo-ticket-picker-help {
  margin: 0 0 9px;
  font-size: 12px;
  line-height: 1.5;
  color: var(--waajo-text-muted);
}
.waajo-ticket-picker-error {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  padding: 9px 11px;
  margin-top: 9px;
  border: 1px solid #fecaca;
  border-radius: 8px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 12px;
  line-height: 1.5;
}
.waajo-ticket-picker-error p {
  margin: 0 0 3px;
}
.waajo-ticket-picker-error p:last-child {
  margin-bottom: 0;
}
</style>



// WEBPACK FOOTER //
// src/views/ticket/components/TicketAttachmentPicker.vue